<template>
  <TabGroup
    :selected-index="defaultIndex"
  >
    <TabList
      class="mb-3 flex items-center space-x-2 p-0 bg-transparent sticky top-20 z-40"
    >
      <Tab
        v-slot="{ selected }"
        as="template"
        @click="sortBusinesses('createdAt')"
      >
        <button
          :class="{
            'bg-red-600 text-white': selected,
            'bg-white text-red-500': !selected
          }"
          class="
            shadow-md font-bold border-2 border-red-600
            hover:bg-red-600 hover:text-white
            flex-1 py-2 px-3 rounded-xl
            focus:outline-none focus:shadow-outline-blue
            transition-all duration-300
            text-xs lg:text-sm
          "
        >
          <span class="hidden md:inline">Most</span> Recent
        </button>
      </Tab>
      <Tab
        v-slot="{ selected }"
        as="template"
        @click="sortBusinesses('rating')"
      >
        <button
          :class="{
            'bg-yellow-500 text-white': selected,
            'bg-white text-gray-600': !selected
          }"
          class="
            shadow-md font-bold border-2
            border-yellow-500 hover:bg-yellow-500
            hover:text-white flex-1 py-2 px-3
            rounded-xl focus:outline-none
            focus:shadow-outline-blue
            transition-all duration-300
            text-xs lg:text-sm
          "
        >
          <span class="hidden md:inline">Most</span>
          <span class="hidden md:inline">Rated</span>
          <span class="md:hidden">Rating</span>
        </button>
      </Tab>
      <Tab
        v-slot="{ selected }"
        as="template"
        @click="sortBusinesses('votes')"
      >
        <button
          :class="{
            'bg-green-600 text-white': selected,
            'bg-white text-green-500': !selected
          }"
          class="
            shadow-md font-bold border-2
            border-green-600 hover:bg-green-600
            hover:text-white flex-1 py-2 px-3
            rounded-xl focus:outline-none
            focus:shadow-outline-blue
            transition-all duration-300
            text-xs lg:text-sm
          "
        >
          <span class="hidden md:inline">Most</span> Popular
        </button>
      </Tab>

      <ListBulletIcon
        v-if="gridLayout"
        class="hidden lg:block h-9 w-9 cursor-pointer shadow-md bg-white rounded-md hover:text-gray-500"
        @click="switchLayout(!gridLayout)"
      />

      <Squares2X2Icon
        v-if="!gridLayout"
        class="hidden lg:block h-9 w-9 cursor-pointer shadow-md bg-white rounded-md hover:text-gray-500"
        @click="switchLayout(!gridLayout)"
      />
      <!--      <Tab class="ml-auto">-->
      <PhotoIcon
        :class="['h-8 w-8 md:h-9 md:w-9 cursor-pointer shadow-md bg-white rounded-md mr-4', showBusinessImage ? 'text-green-600 hover:text-green-500' : 'text-gray-600 hover:text-gray-500']"
        @click="toggleBusinessImage(!showBusinessImage)"
      />
      <!--      </Tab>-->
    </TabList>
    <TabPanels>
      <TabPanel>
        <ContributorPostList
          v-if="props.contributor?.id"
          :layout="gridLayout"
          :show-image="showBusinessImage"
          :businesses="props.businesses"
        />
        <ContributorList
          v-else-if="props.isDisplayingContributors"
          :layout="gridLayout"
          :show-image="showBusinessImage"
          :contributors="props.businesses"
        />
        <PostList
          v-else
          :layout="gridLayout"
          :show-image="showBusinessImage"
          :businesses="props.businesses"
        />
      </TabPanel>
      <TabPanel>
        <ContributorPostList
          v-if="props.contributor?.id"
          :layout="gridLayout"
          :show-image="showBusinessImage"
          :businesses="props.businesses"
        />
        <ContributorList
          v-else-if="props.isDisplayingContributors"
          :layout="gridLayout"
          :show-image="showBusinessImage"
          :contributors="props.businesses"
        />
        <PostList
          v-else
          :layout="gridLayout"
          :show-image="showBusinessImage"
          :businesses="props.businesses"
        />
      </TabPanel>
      <TabPanel>
        <ContributorPostList
          v-if="props.contributor?.id"
          :layout="gridLayout"
          :show-image="showBusinessImage"
          :businesses="props.businesses"
        />
        <ContributorList
          v-else-if="props.isDisplayingContributors"
          :layout="gridLayout"
          :show-image="showBusinessImage"
          :contributors="props.businesses"
        />
        <PostList
          v-else
          :layout="gridLayout"
          :show-image="showBusinessImage"
          :businesses="props.businesses"
        />
      </TabPanel>
    </TabPanels>
  </TabGroup>
</template>

<script setup>
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue';
import { PhotoIcon } from "@heroicons/vue/16/solid";
import PostList from "@/components/PostList.vue";
import { ListBulletIcon, Squares2X2Icon } from '@heroicons/vue/24/outline';
import { computed, onMounted, ref } from 'vue';
import ContributorPostList from '../ContributorPostList.vue';
import { useRoute } from 'vue-router';
import ContributorList from '@/components/ContributorList.vue';
import { useMiscStore } from "@/stores/misc";
import router from "@/router";

// Debug function
const debug = (label, data) => {
  console.log(`[DEBUG] ${label}:`, data);
  return data;
};

const props = defineProps({
  businesses: {
    default: () => [],
    type: [Array, Object],
    validator: (value) => {
      // Allow both arrays and reactive objects with .value
      return Array.isArray(value) || (value && typeof value === 'object' && 'value' in value);
    }
  },
  contributor: {
    default: () => null,
    type: [Object, null]
  },
  isDisplayingContributors: {
    default: false,
    type: Boolean,
  },
});

// Get the actual businesses array, handling both direct array and ref cases
const businessesList = computed(() => {
  const businesses = props.businesses?.value || props.businesses || [];
  debug('Businesses in NavTab', businesses);
  return Array.isArray(businesses) ? businesses : [];
});

// Declare reactive variables
const gridLayout = ref(false);
const showBusinessImage = ref(false);
const route = useRoute();

// Toggle business image visibility
const toggleBusinessImage = (value) => {
  showBusinessImage.value = value;
  localStorage.setItem('showBusinessImage', value);
};

// Switch between grid and list layout
const switchLayout = (value) => {
  gridLayout.value = value;
  localStorage.setItem('layout', value);
};

// Data fetching is now handled by the parent component
  let sortBy = 'createdAt',
      indexes = {
        createdAt: 0,
        rating: 1,
        votes: 2,
      },
      defaultIndex = ref(indexes[sortBy]),
      getDefaultIndex = () => {
        console.log('Default index', indexes[route.query.sortBy], route.query.sortBy);
        defaultIndex.value = indexes[route.query.sortBy];
      },

  sortBusinesses = async (value) => {
    let query = route.query;
    query = { ...query, sortBy: value };
    await router.push({ query });
    console.log(route.params.sector, route.params.category, route.query.sortBy, value);
    route.params.sector !== undefined ? await useMiscStore().updateCurrentSector(route.params.sector) : null;
    route.params.category !== undefined ? await useMiscStore().fetchCategory(route.params.category) : null;
    getDefaultIndex();
  }
  onMounted(() => {
    if (localStorage.getItem('layout')) {
      gridLayout.value = JSON.parse(localStorage.getItem('layout'));
    }

    if (localStorage.getItem('showBusinessImage')) {
      showBusinessImage.value = JSON.parse(localStorage.getItem('showBusinessImage'));
    }
    
    // Debug: Log the received props
    console.group('NavTab Mounted');
    console.log('Received businesses prop:', props.businesses);
    console.log('Processed businesses list:', businessesList.value);
    console.groupEnd();
    getDefaultIndex();
  })
</script>

<style scoped></style>
